// external imports
import { Registry } from "react-registry";

// internal imports
import IService from "core/service/IService";
import IMutationService from "core/service/mutation/IMutationService";
import IQueryService from 'core/service/query/IQueryService';
import { AbstractClass, default as Class } from 'core/util/Class';
import { iDef } from 'core/util/TypeUtils';
import Logger from "./Logger";

/**
 * @overview: Utility class for registering and retrieving services. provides a wrapper around
 * react-registry and an easy way to return different services based on custom conditions.
 *
 * Before using this class see {@link IService} for general documentation on service
 * authoring and consumption. Usage in panels and views should be constrained.
 *
 * @author: jesposito
 *
 * Usage:
 *
 * // get a service
 * {@code const authService = ServiceRegistry.get(IAuthService)}
 *
 * // create a registry
 * {@code const registry = ServiceRegistry.for(IAuthService.class, "authentication", "apollo"); }
 *
 * // register specific implementations
 * {@code registry.register(MyAuthService) }
 * {@code registry.register(AnotherAuthService) }
 */

export interface IRegisterOptions {
	publicId?: string;
	locale?: string;
	lang?: string;
	appVersion?: number;
}

export default class ServiceRegistry<T extends IService> {
	private static REGISTRY_PREFIX: string = "__core:service:";

	private constructor() {
		// this.name = ServiceRegistry.PREFIX + service;
	}

	public static for<T extends IService>(service: AbstractClass<T>): ServiceRegistry<T> {
		return new ServiceRegistry<T>();
	}

	public static get<T extends IService, O extends IRegisterOptions>(service: Class<T> | AbstractClass<T>, options?: O): T {
		// get the registry from the service


		// TODO properly type service construction
		// not great
		const cast = service as any;
		const impl = new cast();

		// verify that the service class has the correct methods
		//

		if(typeof impl.getRegistry() !== "string") {
			Logger.throw("Invalid service group in: %s", service.constructor.name);
		}

		const registry = ServiceRegistry.REGISTRY_PREFIX + impl.getRegistry();

		// if the class specifs the id (service implementtaion)
		// then we use that, otherwise use use getActive;

		let serviceId = null;

		if(typeof impl.getServiceId !== "undefined") {
			serviceId = impl.getServiceId();
		}

		if(typeof serviceId !== "string") {
			serviceId = impl.getActive();
		}

		if(typeof serviceId !== "string") {
			Logger.throw("Invalid Service Id. group: %s", impl.getRegistry());
		}

		let registryArgs = { id: serviceId, registry } as any;

		if(iDef(options)) {
			registryArgs = {...registryArgs, conditions: options};
		}

		return Registry.get(registryArgs) as T;
	}

	public register<C extends T, O extends IRegisterOptions>(serviceImplementation: C) {

		// get the registry from the service
		const registry = ServiceRegistry.REGISTRY_PREFIX + serviceImplementation.getRegistry();

		// get the id from the service
		const id = serviceImplementation.getServiceId();

		// get the conditions from the service
		const options = serviceImplementation.getConditions();

		Logger.debug("Registering { group: %s, name: %s }", registry, id);

		// add it to the registry
		Registry.register(serviceImplementation, {id, registry, conditions: options});
	}

	public static getQueryService(): IQueryService {
		return ServiceRegistry.get(IQueryService);
	}

	public static getMutationService(): IMutationService {
		return ServiceRegistry.get(IMutationService);
	}
}



// WEBPACK FOOTER //
// ./src/core/util/ServiceRegistry.ts